import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { NavigationError, Router, RouterModule, RouterOutlet } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { ClientSelectorComponent } from './client-selector/client-selector.component';
import { environment } from '../environments/environment';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { AccessToken } from './access-token';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, filter, finalize, forkJoin, fromEvent, interval, merge, of } from 'rxjs';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialog } from './error-dialog/error-dialog';
import { AssessmentDomainService } from './domain-services/assessment-domain.service';
import { PackageDomainService } from './domain-services/package-domain.service';
import { ParticipantDomainService } from './domain-services/participant-domain.service';
import { ProjectDomainService } from './domain-services/project-domain.service';
import { ReportDomainService } from './domain-services/report-domain.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatSidenavModule,
    MatListModule,
    MatSnackBarModule,
    ClientSelectorComponent,
    RouterModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
})
export class App implements OnInit {
  protected readonly title = signal('Halo Lite');

  router = inject(Router);
  location = inject(Location);
  private oauth = inject(OAuthService);
  private readonly assessmentDomain = inject(AssessmentDomainService);
  private readonly packageDomain = inject(PackageDomainService);
  private readonly participantDomain = inject(ParticipantDomainService);
  private readonly projectDomain = inject(ProjectDomainService);
  private readonly reportDomain = inject(ReportDomainService);
  private readonly isClientSwitching = signal(false);

  errorEvent = toSignal(
    merge(this.router.events, this.oauth.events).pipe(
      filter((e) => e instanceof NavigationError || e instanceof OAuthErrorEvent),
    ),
  );

  documentEvent = toSignal(
    merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'mousedown'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'scroll'),
      fromEvent(document, 'touchstart'),
      fromEvent(document, 'click'),
    ),
  );
  clock = toSignal(interval(1 * 1000));

  isNavigating = computed(() => {
    if (this.isClientSwitching()) {
      return true;
    }

    const nav = this.router.currentNavigation();
    if (nav) {
      return nav?.extras?.state ? nav.extras.state['showLoading'] : true;
    }
    return false;
  });

  private last_action: number = Date.now();
  readonly dialog = inject(MatDialog);

  searchOpen = signal(false);
  searchQuery = signal('');

  constructor() {
    //Tap into the document events
    effect(() => {
      if (this.documentEvent()) this.last_action = Date.now();
    });

    //Tap into our clock to check for inactivity
    effect(() => {
      if (this.clock()) {
        const now = Date.now();
        const idleFor = now - this.last_action;
        if (idleFor > 15 * 60 * 1000) {
          console.log('User is idle for more than 15 minutes, logging out.');
          this.oauth.logOut();
        }
      }
    });

    effect(() => {
      if (this.errorEvent()) {
        this.dialog.open(ErrorDialog, {
          data: this.errorEvent(),
        });
      }
    });
  }

  ngOnInit() {
    this.clients = new AccessToken(this.oauth.getAccessToken()).clients;
    if (this.clients && this.clients.length > 0) {
      sessionStorage.setItem('activeClient', this.clients[0]);
    }
  }

  clients: string[] | null = null;
  policyUrl = environment.policy_url;
  termsUrl = environment.terms_url;

  toggleSearch(input: HTMLInputElement) {
    const open = !this.searchOpen();
    this.searchOpen.set(open);
    if (open) {
      setTimeout(() => input.focus());
    }
  }

  onSearchInput(event: Event) {
    const target = event.target as HTMLInputElement | null;
    this.searchQuery.set(target?.value ?? '');
  }

  submitSearch() {
    const query = this.searchQuery().trim();
    if (!query) {
      return;
    }

    this.router.navigate(['global-search'], {
      queryParams: { q: query },
    });
  }

  logout() {
    this.oauth.logOut();
  }

  setActiveClient(client: string) {
    const currentClient = sessionStorage.getItem('activeClient');
    if (currentClient === client) {
      return;
    }

    this.isClientSwitching.set(true);
    sessionStorage.setItem('activeClient', client);
    this.clearTenantCaches();
    this.router.navigate(['/'], { state: { showLoading: true } });
    this.refreshDashboardData()
      .pipe(finalize(() => this.isClientSwitching.set(false)))
      .subscribe();
  }

  private clearTenantCaches(): void {
    this.assessmentDomain.clearCache();
    this.packageDomain.clearCache();
    this.participantDomain.clearCache();
    this.projectDomain.clearCache();
    this.reportDomain.clearCache();
  }

  private refreshDashboardData() {
    return forkJoin([
      this.projectDomain.loadProjects().pipe(catchError(() => of([]))),
      this.assessmentDomain.loadAssessments().pipe(catchError(() => of([]))),
    ]);
  }
}
